import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, formatPrice } from "@/lib/api";
import {
  Clock, CheckCircle2, Flag, EyeOff, Trash2, UserX, ShieldAlert, MessageCircle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const REASON_LABELS = {
  spam: "Spam",
  fraud: "Fraude",
  duplicate: "Doublon",
  inappropriate: "Inapproprié",
  wrong_info: "Infos erronées",
  other: "Autre",
};

const ACTION_LABELS = {
  dismiss: { label: "Rejeter le signalement", icon: CheckCircle2, color: "#9a9a9a", desc: "L'annonce reste publique." },
  hide_listing: { label: "Cacher l'annonce", icon: EyeOff, color: "#F4B400", desc: "Statut → hidden. Réversible." },
  delete_listing: { label: "Supprimer l'annonce", icon: Trash2, color: "#D6202C", desc: "Statut → deleted. Irréversible." },
  ban_user: { label: "Bannir l'utilisateur", icon: UserX, color: "#7a0a12", desc: "Désactive le compte + cache toutes ses annonces." },
};

export default function AdminModeration() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, resolved: 0, all: 0 });
  const [filter, setFilter] = useState("pending");
  const [notesDraft, setNotesDraft] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { reportId, action }

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/login");
    if (user.role !== "admin") {
      toast.error("Accès réservé aux administrateurs");
      return navigate("/dashboard");
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, filter]);

  const refresh = async () => {
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get("/admin/reports", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/reports/counts"),
      ]);
      setItems(list);
      setCounts(c);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const performAction = async (reportId, action) => {
    setBusyId(reportId);
    try {
      await api.post(`/admin/reports/${reportId}/action`, {
        action,
        notes: notesDraft[reportId] || null,
      });
      toast.success("Action effectuée");
      setNotesDraft({ ...notesDraft, [reportId]: "" });
      setConfirmAction(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <Layout withFooter={false}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-moderation">
        <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Admin</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2 flex items-center gap-2">
          Modération <ShieldAlert className="w-8 h-8 text-[#D6202C]" />
        </h1>
        <p className="text-muted-foreground mb-8">
          Examinez les signalements d'annonces remontés par les utilisateurs.
        </p>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="moderation-filters">
          {[
            ["pending", "En attente", Clock, "#F4B400"],
            ["resolved", "Traités", CheckCircle2, "#11783A"],
            ["all", "Tous", Flag, "#9a9a9a"],
          ].map(([k, label, Icon, color]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`moderation-filter-${k}`}
              className={`px-4 h-10 rounded-full text-sm font-medium border transition flex items-center gap-1.5 ${
                filter === k ? "border-transparent text-white" : "bg-white border-[#E5E5E5] hover:bg-[#FAFAFA]"
              }`}
              style={filter === k ? { background: color } : {}}
            >
              <Icon className="w-4 h-4 -mt-0.5" /> {label}
              <span
                data-testid={`moderation-count-${k}`}
                className={`text-[11px] tabular-nums px-1.5 py-0.5 rounded-full ${
                  filter === k ? "bg-white/25" : "bg-[#F5F0EB] text-muted-foreground"
                }`}
              >
                {counts[k] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-12 text-center" data-testid="moderation-empty">
            <CheckCircle2 className="w-10 h-10 text-[#11783A]/40 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun signalement à examiner.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="moderation-list">
            {items.map((r) => (
              <ReportCard
                key={r.id}
                report={r}
                isBusy={busyId === r.id}
                notes={notesDraft[r.id] || ""}
                onNotesChange={(v) => setNotesDraft({ ...notesDraft, [r.id]: v })}
                onAction={(action) => setConfirmAction({ reportId: r.id, action })}
              />
            ))}
          </div>
        )}
      </div>

      {confirmAction && (
        <ConfirmActionModal
          action={confirmAction.action}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => performAction(confirmAction.reportId, confirmAction.action)}
        />
      )}
    </Layout>
  );
}

const ReportCard = ({ report, isBusy, notes, onNotesChange, onAction }) => {
  const listing = report.listing;
  const owner = report.owner;
  const resolved = report.status === "resolved";

  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E5E5] p-5"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Listing preview */}
        <Link
          to={`/listings/${report.listing_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 group"
        >
          <div className="w-full md:w-32 aspect-square rounded-xl overflow-hidden bg-[#F5F0EB]">
            {listing?.photos?.[0] ? (
              <img src={listing.photos[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#D6202C]/30">
                <Flag className="w-8 h-8" />
              </div>
            )}
          </div>
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEF2F3] text-[#D6202C] font-medium"
              data-testid="report-reason-tag"
            >
              {REASON_LABELS[report.reason] || report.reason}
            </span>
            {resolved && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#11783A]/10 text-[#11783A] font-medium">
                Traité · {ACTION_LABELS[report.action_taken]?.label || report.action_taken}
              </span>
            )}
            {listing?.status === "hidden" && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F4B400]/15 text-[#7a5a00] font-medium">
                Cachée
              </span>
            )}
            {listing?.status === "deleted" && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#9a9a9a]/15 text-[#5C5C5C] font-medium">
                Supprimée
              </span>
            )}
            {owner?.banned && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#7a0a12]/15 text-[#7a0a12] font-medium">
                Annonceur banni
              </span>
            )}
          </div>

          <h3 className="font-heading font-medium text-lg leading-tight mb-1 truncate">
            <Link to={`/listings/${report.listing_id}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#D6202C] inline-flex items-center gap-1">
              {report.listing_title} <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </h3>

          <div className="text-xs text-muted-foreground mb-3 flex items-center gap-3 flex-wrap">
            {listing?.city && <span>{listing.city}</span>}
            {listing?.price && <span>· {formatPrice(listing.price, listing.currency, listing.type)}</span>}
            {owner && (
              <span>·
                <Link to={`/agence/${report.listing_owner_id}`} target="_blank" rel="noopener noreferrer" className="ml-1 hover:text-[#D6202C]">
                  {owner.company || owner.name}
                </Link>
              </span>
            )}
            <span>· Signalé par <strong className="text-[#1A1A1A]">{report.reporter_name}</strong></span>
          </div>

          {report.comment && (
            <div className="text-sm bg-[#F5F0EB] rounded-xl p-3 mb-3 flex gap-2">
              <MessageCircle className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <p className="leading-relaxed">{report.comment}</p>
            </div>
          )}

          {resolved && report.review_notes && (
            <div className="text-xs text-muted-foreground mb-3">
              <strong>Note admin :</strong> {report.review_notes}
            </div>
          )}

          {!resolved && (
            <>
              <Textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Note interne (facultatif, max 500 caractères)"
                maxLength={500}
                rows={2}
                className="rounded-xl mb-3 text-sm"
                data-testid={`moderation-notes-${report.id}`}
              />
              <div className="flex flex-wrap gap-2">
                {Object.entries(ACTION_LABELS).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <Button
                      key={key}
                      onClick={() => onAction(key)}
                      disabled={isBusy}
                      variant="outline"
                      data-testid={`moderation-action-${key}-${report.id}`}
                      className="rounded-xl text-xs h-9 px-3 border-[#E5E5E5]"
                      style={{ color: meta.color, borderColor: `${meta.color}40` }}
                    >
                      <Icon className="w-3.5 h-3.5 mr-1.5" /> {meta.label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmActionModal = ({ action, onCancel, onConfirm }) => {
  const meta = ACTION_LABELS[action];
  const Icon = meta.icon;
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="moderation-confirm-modal"
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: `${meta.color}15`, color: meta.color }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <h3 className="font-heading text-xl font-medium mb-2">Confirmer : {meta.label} ?</h3>
        <p className="text-sm text-muted-foreground mb-6">{meta.desc}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl">
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            data-testid="moderation-confirm-btn"
            className="flex-1 rounded-xl text-white"
            style={{ background: meta.color }}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </div>
  );
};
